import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

import {HugeiconsIcon} from "@hugeicons/react";
import {Delete02Icon, Maximize01Icon} from "@hugeicons/core-free-icons";
import React, {useContext, useEffect, useMemo, useState} from "react";
import {Autocomplete} from "@mui/material";
import {Context} from "../../../../../context/AppContext";
import MarkdownEditorModal from "../../../../../components/modals/MarkdownEditorModal";


const emptyForm = {name: "", description: "", content: "", tools: []};

export const CreateSkillModal = ({open, project, skill, handleClose, handleSubmit, onRemove}) => {
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const {state} = useContext(Context);
    const {mcpServers, mcpLoading} = state;
    const [expandPrompt, setExpandPrompt] = useState(false);

    const isEdit = !!skill?.id;
    const availableTools = useMemo(() => {
        return mcpServers
            .flatMap((server) =>
                (server.tools || []).map((tool) => ({
                    id: `${server.name}:${tool.name}`,
                    name: tool.name,
                    description: tool.description,
                    inputSchema: tool.inputSchema,
                    mcp_server: server.name,
                    mcp_server_id: server.id,
                }))
            )
            .sort((a, b) => a.mcp_server.localeCompare(b.mcp_server));
    }, [mcpServers]);

    useEffect(() => {
        if (open) {
            setForm(
                skill
                    ? {
                        name: skill.name || "",
                        description: skill.description || "",
                        content: skill.content || "",
                        tools: skill.tools || [],
                    }
                    : emptyForm
            );
            setErrors({});
        }
    }, [open, skill]);

    const setField = (key) => (e) => {
        setForm((prev) => ({...prev, [key]: e.target.value}));
        setErrors((prev) => ({...prev, [key]: undefined}));
    };

    const validate = () => {
        const next = {};
        if (!form.name.trim()) next.name = "Name is required";
        if (!form.content.trim()) next.content = "System prompt is required";
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const onSave = async () => {
        if (!validate()) return;
        try {
            setSaving(true);
            const payload = {
                id: skill?.id || crypto.randomUUID(),
                name: form.name.trim(),
                description: form.description.trim(),
                content: form.content,
                tools: form.tools || []
            };
            await handleSubmit(payload);
            handleClose();
        } catch (e) {
            console.error("Failed to save skill:", e);
        } finally {
            setSaving(false);
        }
    };

    const onDelete = async () => {
        if (!skill?.id) return;
        try {
            setSaving(true);
            await onRemove(skill.id);
            handleClose();
        } catch (e) {
            console.error("Failed to delete skill:", e);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{fontSize: 16, fontWeight: 600}}>
                    {isEdit ? "Edit Skill" : "New Skill"}
                </DialogTitle>
                <DialogContent>
                    <Stack gap={2} sx={{mt: 1}}>
                        <TextField
                            label="Name"
                            size="small"
                            fullWidth
                            value={form.name}
                            onChange={setField("name")}
                            error={!!errors.name}
                            helperText={errors.name}
                            InputProps={{sx: {borderRadius: 0}}}
                        />
                        <TextField
                            label="Description"
                            size="small"
                            fullWidth
                            value={form.description}
                            onChange={setField("description")}
                            placeholder="When should this skill be used?"
                            InputProps={{sx: {borderRadius: 0}}}
                        />
                        <Autocomplete
                            multiple
                            size="small"
                            options={availableTools}
                            groupBy={(option) => option.mcp_server}
                            getOptionLabel={(option) => option.name}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            value={form.tools}
                            onChange={(_, newValue) => setForm((prev) => ({...prev, tools: newValue}))}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Tools"
                                    placeholder="Select allowed tools"
                                    InputProps={{...params.InputProps, sx: {borderRadius: 0}}}
                                />
                            )}
                            renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                    <Stack>
                                        <Typography variant="body2">{option.name}</Typography>
                                        {option.description && (
                                            <Typography variant="caption" color="text.secondary" noWrap sx={{maxWidth: 400}}>
                                                {option.description.replace(/\n/g, " ").trim()}
                                            </Typography>
                                        )}
                                    </Stack>
                                </li>
                            )}
                        />
                        <Box>
                            <Stack direction={"row"} gap={1} justifyContent={"space-between"} alignItems={"center"} sx={{pb: 0.5}}>
                                <Typography fontWeight={700} fontSize={13}>System Prompt</Typography>
                                <IconButton size="small" onClick={() => setExpandPrompt(true)}>
                                    <HugeiconsIcon icon={Maximize01Icon} size={15}/>
                                </IconButton>
                            </Stack>
                            <TextField
                                size="small"
                                fullWidth
                                multiline
                                minRows={6}
                                maxRows={9}
                                value={form.content}
                                onChange={setField("content")}
                                error={!!errors.content}
                                helperText={errors.content}
                                placeholder="The prompt text / instructions..."
                                InputProps={{sx: {borderRadius: 0, fontFamily: "monospace", fontSize: 13}}}
                                onClick={() => setExpandPrompt(true)}
                            />
                            <Typography variant="caption" color="text.secondary">
                                This is the reusable prompt that gets injected when the skill is used.
                            </Typography>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{px: 3, pb: 2, justifyContent: "space-between"}}>
                    <Box>
                        {isEdit && (
                            <IconButton color="error" onClick={onDelete} disabled={saving}>
                                <HugeiconsIcon icon={Delete02Icon} size={18}/>
                            </IconButton>
                        )}
                    </Box>
                    <Stack direction="row" gap={1}>
                        <Button
                            variant="ghost"
                            onClick={handleClose}
                            disabled={saving}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="modal"
                            onClick={onSave}
                            disabled={saving}
                        >
                            {isEdit ? "Save" : "Create"}
                        </Button>
                    </Stack>
                </DialogActions>
            </Dialog>

            <MarkdownEditorModal
                open={expandPrompt}
                onClose={() => setExpandPrompt(false)}
                title="System prompt"
                value={form.content}
                onChange={setField("content")}
            />
        </>
    );
};

export default CreateSkillModal;
